// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/dabPlanner.js
//
// Plans dabs for schema-v2 brushes. Pure and deterministic for a given
// stroke seed, so local strokes, live replays and previews place the same
// dabs. Each dab carries everything the GPU needs:
//   size      diameter in document px after every size dynamic
//   scaleX/Y  roundness and flips (negative = mirrored)
//   flow      coverage step k = mask * flow (alpha-darken factor)
//   opacity   coverage target for this dab (alpha-darken target);
//             in build-up mode the step and target collapse into one alpha
//   rotation  radians
//   colour    [r, g, b] 0..1 when colour dynamics are active, else null
//   grainX/Y  grain origin in document px (moving grain follows the dab)
//   distance  stroke distance at the dab, for end taper re-rendering
import { evaluateBakedCurve } from './curves.js';

const TAU = Math.PI * 2;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function unitRandom(seed) {
  let t = (Math.floor(Number(seed) || 0) + 0x6d2b79f5) | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function pressureOf(point) {
  const p = Number(point && point.pressure);
  return Number.isFinite(p) && p > 0 ? clamp(p, 0, 1) : 1;
}

// Pen tilt away from vertical, 0 (upright) .. PI/2 (flat).
export function tiltOf(point) {
  const altitude = Number(point && point.altitudeAngle);
  if (Number.isFinite(altitude) && altitude > 0) return clamp(Math.PI / 2 - altitude, 0, Math.PI / 2);
  const tx = (Number(point && point.tiltX) || 0) * (Math.PI / 180);
  const ty = (Number(point && point.tiltY) || 0) * (Math.PI / 180);
  if (!tx && !ty) return 0;
  const tanX = Math.tan(clamp(tx, -1.55, 1.55));
  const tanY = Math.tan(clamp(ty, -1.55, 1.55));
  return clamp(Math.atan(Math.sqrt(tanX * tanX + tanY * tanY)), 0, Math.PI / 2);
}

export function azimuthOf(point, fallback = 0) {
  const azimuth = Number(point && point.azimuthAngle);
  if (Number.isFinite(azimuth)) return azimuth;
  const tx = Number(point && point.tiltX) || 0;
  const ty = Number(point && point.tiltY) || 0;
  if (!tx && !ty) return fallback;
  return Math.atan2(ty, tx);
}

function pressureResponse(response, pressure) {
  if (!response || !(response.amount > 0)) return 1;
  const shaped = response.curve && response.curve.length ? evaluateBakedCurve(response.curve, pressure, pressure) : pressure;
  return 1 - response.amount * (1 - shaped);
}

// Channel multiplier for pressure, tilt, speed, jitter and its minimum.
function channelValue(channel, input) {
  if (!channel) return 1;
  let value = pressureResponse(channel.pressure, input.pressure);
  if (channel.tilt && channel.tilt.amount) {
    const start = clamp(channel.tilt.angle || 0, 0, Math.PI / 2 - 0.01);
    const t = clamp((input.tilt - start) / (Math.PI / 2 - start), 0, 1);
    value *= clamp(1 - Math.abs(channel.tilt.amount) * (channel.tilt.amount > 0 ? t : 1 - t), 0, 1);
  }
  if (channel.speed) {
    const s = clamp(input.speed / 3, 0, 1);
    value *= channel.speed < 0 ? 1 - Math.abs(channel.speed) * s : 1 - channel.speed * (1 - s);
  }
  if (channel.jitter > 0) value *= 1 - channel.jitter * input.random;
  const minimum = channel.minimum || 0;
  return clamp(minimum + (1 - minimum) * value, 0, 4);
}

// HSL jitter around the base colour; 0..1 per channel range.
function hexToRgb(hex) {
  const text = String(hex || '#000000').replace('#', '');
  const full = text.length === 3 ? text.split('').map((c) => c + c).join('') : text.padEnd(6, '0');
  const value = parseInt(full.slice(0, 6), 16) || 0;
  return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
}

function rgbToHsl([r, g, b]) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s, l];
}

function hslToRgb([h, s, l]) {
  if (s <= 0) return [l, l, l];
  const hue = (p, q, t) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue(p, q, h + 1 / 3), hue(p, q, h), hue(p, q, h - 1 / 3)];
}

function jitterColour(hsl, jitter, seed) {
  if (!jitter) return hsl;
  const [h, s, l] = hsl;
  const r1 = unitRandom(seed + 11) * 2 - 1;
  const r2 = unitRandom(seed + 23) * 2 - 1;
  const r3 = unitRandom(seed + 37);
  const r4 = unitRandom(seed + 41);
  return [
    (h + r1 * jitter.hue * 0.5 + 1) % 1,
    clamp(s + r2 * jitter.saturation, 0, 1),
    clamp(l + r3 * jitter.lightness * (1 - l) - r4 * jitter.darkness * l, 0, 1),
  ];
}

function hasColourJitter(jitter) {
  return !!jitter && (jitter.hue > 0.001 || jitter.saturation > 0.001 || jitter.lightness > 0.001 || jitter.darkness > 0.001);
}

export function brushUsesColourDynamics(brush) {
  const colour = brush && brush.brushDynamics && brush.brushDynamics.colour;
  return !!colour && (hasColourJitter(colour.perDab) || hasColourJitter(colour.perStroke));
}

export function isV2ImportedBrush(brush) {
  return !!(brush && brush.brushDynamics && brush.brushSettings);
}

export function brushHasEndTaper(brush) {
  const taper = brush && brush.brushDynamics && brush.brushDynamics.taper;
  return !!taper && taper.end > 0 && (taper.size > 0 || taper.opacity > 0);
}

// Settings for the GPU stroke session of a v2 brush.
export function importedStrokeSessionOptions(brush, options = {}) {
  const settings = (brush && brush.brushSettings) || {};
  const uiOpacity = clamp(Number(options.opacity == null ? 1 : options.opacity), 0, 1);
  const isEraser = !!options.isEraser || settings.eraser === true;
  return {
    strokeOpacity: settings.opacityMode === 'dab' ? uiOpacity : uiOpacity * clamp(Number(settings.opacity) || 1, 0, 1),
    color: options.color || options.strokeColor || '#000000',
    isEraser,
    useColourTexture: !isEraser && (brushUsesColourDynamics(brush) || brush.tipColour === 'image'),
  };
}

// Taper length in px for a 0..1 Procreate-style length at this brush size.
export function taperLengthPx(length, size) {
  return clamp(length, 0, 1) * Math.max(24, size * 8);
}

export function taperFactor(taper, distanceFromStart, distanceToEnd, size) {
  if (!taper) return 1;
  let factor = 1;
  if (taper.start > 0 && distanceFromStart != null) {
    const length = taperLengthPx(taper.start, size);
    if (distanceFromStart < length) factor = Math.min(factor, distanceFromStart / length);
  }
  if (taper.end > 0 && distanceToEnd != null) {
    const length = taperLengthPx(taper.end, size);
    if (distanceToEnd < length) factor = Math.min(factor, distanceToEnd / length);
  }
  return clamp(factor, 0, 1);
}

export function createStrokeState(options = {}) {
  return {
    distanceToNext: 0,
    lastAngle: 0,
    strokeDistance: 0,
    lastTime: 0,
    speed: 0,
    dabLog: options.logDabs ? [] : null,
  };
}

function interpolatePoint(a, b, t) {
  const point = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  ['pressure', 'tiltX', 'tiltY', 'altitudeAngle', 'azimuthAngle', 'twist'].forEach((key) => {
    const av = Number(a[key]);
    const bv = Number(b[key]);
    if (Number.isFinite(av) || Number.isFinite(bv)) {
      const sa = Number.isFinite(av) ? av : bv;
      const sb = Number.isFinite(bv) ? bv : sa;
      point[key] = sa + (sb - sa) * t;
    }
  });
  return point;
}

// Plans dabs between two points. `options.size` is the user's brush size in
// px; `options.color` the base colour; `state` persists across segments.
export function planDabs(start, end, brush, options, state, dabIndexStart = 0, includeStart = false, now = Date.now()) {
  const dyn = brush.brushDynamics || {};
  const settings = brush.brushSettings || {};
  const texture = brush.brushTexture || null;
  const seedBase = Number(options.strokeSeed) || 1;
  const baseSize = Math.max(0.5, (Number(options.size) || 1) * (Number(settings.tipScale) || 1));
  const spacingRatio = clamp(Number(settings.spacing) || 0.1, 0.005, 4);
  const stroke = state || createStrokeState();
  if (!Number.isFinite(stroke.strokeDistance)) stroke.strokeDistance = 0;
  if (!Number.isFinite(stroke.speed)) stroke.speed = 0;
  if (!Number.isFinite(stroke.lastTime)) stroke.lastTime = 0;
  const dabs = [];
  let dabIndex = Math.max(0, Number(dabIndexStart) || 0);
  const rotationDyn = dyn.rotation || {};
  const scatter = dyn.scatter || {};
  const count = dyn.count || { value: 1, jitter: 0 };
  const colourDyn = dyn.colour || {};
  const baseRgb = hexToRgb(options.color || options.strokeColor || '#000000');
  const baseHsl = rgbToHsl(baseRgb);
  const colourActive = hasColourJitter(colourDyn.perDab) || hasColourJitter(colourDyn.perStroke);
  const strokeHsl = hasColourJitter(colourDyn.perStroke) ? jitterColour(baseHsl, colourDyn.perStroke, seedBase * 7 + 3) : baseHsl;
  const strokeAngleOffset = rotationDyn.randomPerStroke ? unitRandom(seedBase + 501) * TAU : 0;
  const grainOffset = texture && texture.offsetJitter
    ? { x: unitRandom(seedBase + 601) * 4096, y: unitRandom(seedBase + 607) * 4096 }
    : { x: 0, y: 0 };
  const isWash = settings.opacityMode !== 'dab';

  const dx = end ? end.x - start.x : 0;
  const dy = end ? end.y - start.y : 0;
  const length = Math.hypot(dx, dy);
  const travelAngle = length > 0.001 ? Math.atan2(dy, dx) : stroke.lastAngle || 0;
  if (length > 0.001) stroke.lastAngle = travelAngle;
  if (stroke.lastTime && now > stroke.lastTime && length > 0) {
    const instant = length / (now - stroke.lastTime);
    stroke.speed = stroke.speed ? stroke.speed * 0.7 + instant * 0.3 : instant;
  }
  stroke.lastTime = now;

  const spacingAt = (point) => {
    const pressure = pressureOf(point);
    const sizeHere = baseSize * channelValue(dyn.size, { pressure, tilt: tiltOf(point), speed: stroke.speed, random: 0.5 });
    const spacingMul = pressureResponse(dyn.spacing && dyn.spacing.pressure, pressure);
    return Math.max(0.5, Math.max(1, sizeHere) * spacingRatio * Math.max(0.05, spacingMul));
  };

  const emit = (point, distance) => {
    const seed = seedBase * 131 + dabIndex * 977;
    const pressure = pressureOf(point);
    const tilt = tiltOf(point);
    const input = { pressure, tilt, speed: stroke.speed, random: unitRandom(seed + 1) };
    const taper = taperFactor(dyn.taper, distance, null, baseSize);
    const taperSize = dyn.taper && dyn.taper.size > 0 ? 1 - dyn.taper.size * (1 - taper) : 1;
    const taperOpacity = dyn.taper && dyn.taper.opacity > 0 ? 1 - dyn.taper.opacity * (1 - taper) : 1;
    const size = baseSize * channelValue(dyn.size, input) * taperSize;
    const opacityInput = { ...input, random: unitRandom(seed + 2) };
    const opacityDyn = channelValue(dyn.opacity, opacityInput) * taperOpacity;
    const flowDyn = channelValue(dyn.flow, { ...input, random: unitRandom(seed + 3) });
    const follow = Number(rotationDyn.follow) || 0;
    let rotation = (Number(settings.angle) || 0) + strokeAngleOffset + travelAngle * follow;
    if (rotationDyn.azimuth) rotation += azimuthOf(point, 0);
    if (rotationDyn.jitter > 0) rotation += (unitRandom(seed + 4) - 0.5) * rotationDyn.jitter;
    const scatterScale = (scatter.pressure ? pressure : 1) * size;
    const perpendicular = (Number(scatter.perpendicular) || 0) * scatterScale * (unitRandom(seed + 5) * 2 - 1);
    const along = (Number(scatter.along) || 0) * scatterScale * (unitRandom(seed + 6) * 2 - 1);
    const cos = Math.cos(travelAngle);
    const sin = Math.sin(travelAngle);
    const cx = point.x + cos * along - sin * perpendicular;
    const cy = point.y + sin * along + cos * perpendicular;
    const shapes = count.value > 1 ? Math.max(1, Math.round(count.value - (count.jitter || 0) * unitRandom(seed + 7) * (count.value - 1))) : 1;
    const flipX = rotationDyn.flipXJitter && unitRandom(seed + 8) < 0.5 ? -1 : 1;
    const flipY = rotationDyn.flipYJitter && unitRandom(seed + 9) < 0.5 ? -1 : 1;
    const roundness = clamp(Number(settings.roundness) || 1, 0.02, 1);
    const flow = clamp((Number(settings.flow) || 1) * flowDyn, 0, 1);
    const opacityTarget = clamp(opacityDyn, 0, 1);
    const colour = colourActive ? hslToRgb(jitterColour(strokeHsl, colourDyn.perDab, seed + 13)) : null;
    const movement = texture ? (texture.mode === 'moving' ? 1 : Number(texture.movement) || 0) : 0;
    for (let shape = 0; shape < shapes; shape += 1) {
      const shapeSeed = seed + shape * 7919;
      const shapeRotation = shape === 0 ? rotation : rotation + unitRandom(shapeSeed + 10) * TAU;
      const dab = {
        x: cx,
        y: cy,
        size: Math.max(0.25, size),
        sizeMultiplier: Math.max(0.25, size) / Math.max(0.5, Number(options.size) || 1),
        scaleX: flipX,
        scaleY: roundness * flipY,
        rotation: shapeRotation,
        // Build-up: every dab composites at opacity x flow (Krita build-up,
        // MyPaint). Wash/glaze: the preset opacity caps the whole stroke.
        flow: isWash ? flow : clamp(flow * opacityTarget * (Number(settings.opacity) || 1), 0, 1),
        opacity: isWash ? opacityTarget : 1,
        pressure,
        seed: shapeSeed,
        tipIndex: dabIndex,
        colour,
        grainX: cx * movement - grainOffset.x,
        grainY: cy * movement - grainOffset.y,
        distance,
      };
      dabs.push(dab);
      if (stroke.dabLog && stroke.dabLog.length < 200000) stroke.dabLog.push(dab);
    }
    dabIndex += 1;
  };

  if (includeStart) {
    emit(start, stroke.strokeDistance);
    stroke.distanceToNext = spacingAt(start);
  }
  if (!end || length <= 0.0001) return { dabs, dabIndex, distanceToNext: stroke.distanceToNext, lastAngle: stroke.lastAngle };

  let distanceToNext = Math.max(0.0001, Number(stroke.distanceToNext) || 0);
  let guard = 0;
  while (distanceToNext <= length && guard < 20000) {
    const t = distanceToNext / length;
    const point = interpolatePoint(start, end, t);
    emit(point, stroke.strokeDistance + distanceToNext);
    distanceToNext += spacingAt(point) * (settings.spacingJitter > 0 ? 1 + (unitRandom(seedBase + dabIndex * 53) - 0.5) * settings.spacingJitter : 1);
    guard += 1;
  }
  stroke.distanceToNext = Math.max(0.0001, distanceToNext - length);
  stroke.strokeDistance += length;
  return { dabs, dabIndex, distanceToNext: stroke.distanceToNext, lastAngle: stroke.lastAngle };
}

// Applies the end taper to a finished stroke's logged dabs. Returns null when
// the brush has no end taper, so callers skip the re-render.
export function applyEndTaper(dabLog, brush, options) {
  const taper = brush && brush.brushDynamics && brush.brushDynamics.taper;
  if (!taper || !(taper.end > 0) || !(taper.size > 0 || taper.opacity > 0) || !Array.isArray(dabLog) || !dabLog.length) return null;
  const total = dabLog[dabLog.length - 1].distance || 0;
  const baseSize = Math.max(0.5, (Number(options.size) || 1) * (Number(brush.brushSettings && brush.brushSettings.tipScale) || 1));
  return dabLog.map((dab) => {
    const factor = taperFactor({ start: 0, end: taper.end }, null, Math.max(0, total - (dab.distance || 0)), baseSize);
    if (factor >= 1) return dab;
    const sizeFactor = taper.size > 0 ? 1 - taper.size * (1 - factor) : 1;
    const opacityFactor = taper.opacity > 0 ? 1 - taper.opacity * (1 - factor) : 1;
    return {
      ...dab,
      size: Math.max(0.25, dab.size * sizeFactor),
      sizeMultiplier: dab.sizeMultiplier * sizeFactor,
      opacity: dab.opacity * opacityFactor,
      flow: brush.brushSettings && brush.brushSettings.opacityMode === 'dab' ? dab.flow * opacityFactor : dab.flow,
    };
  });
}
